/**
 * <meditation-audio> custom element.
 *
 * Property-driven (not attribute-driven) so Elm's VDOM diff only writes when
 * values actually change — avoids spurious seeks and play() calls on every render.
 *
 * Discrete state events bubble as CustomEvents (Elm decodes via Html.Events.on).
 * High-frequency `timeupdate` is throttled and dispatched on `window` so it can
 * be bridged to a port instead of thrashing the VDOM.
 */

import { getDeviceTier } from "./perf-budget.ts";
import { publishVoice } from "./frame-bus.ts";
import { isIOS } from "./ios.ts";

const TIMEUPDATE_THROTTLE_MS = 250 as const;
const SEEK_THRESHOLD_SECONDS = 0.5 as const;

/** Lock-screen scrubber cadence. Was updated on every 250ms position tick —
 *  4×/s for a 20-minute track, where the OS only ever renders a coarse bar.
 *  1 Hz is indistinguishable and costs a quarter as much. */
const POSITION_STATE_EVERY_N_TICKS = 4 as const;

/** Smallest --audio-level delta worth a write to :root. A custom-property
 *  write on documentElement invalidates style for the whole subtree, so
 *  during the long silences between Lisa's phrases we were paying a full
 *  document style invalidation 30×/s to re-write "0.000" over "0.000". */
const LEVEL_WRITE_EPSILON = 0.01 as const;

/** Watchdog cadence while playback is intended. Cheap: one interval, a few
 *  property reads per tick. iOS throttles background timers to ≥1s — fine,
 *  the watchdog only needs to be roughly periodic. */
const WATCHDOG_MS = 2500 as const;

/** currentTime frozen this long while nominally playing = a genuine stall
 *  (buffer starvation, dropped connection) → hard-recover via load()+seek. */
const STALL_THRESHOLD_MS = 8000 as const;

/** Bounded so a truly dead stream ends in an honest paused/errored UI
 *  instead of an infinite silent retry loop. */
const MAX_RESUME_ATTEMPTS = 10 as const;
const MAX_HARD_RECOVERIES = 3 as const;

/** Web Audio analyser FFT bin count — 256 gives 128 frequency bins,
 *  cheap enough to sample at 60Hz on a phone. */
const ANALYSER_FFT_SIZE = 256 as const;

/** Smoothing factor for the level envelope. Lower = snappier. */
const LEVEL_SMOOTHING = 0.35 as const;

/** CSS custom property the audio level is written into on
 *  document.documentElement (`:root`). Read via `var(--audio-level)` in
 *  styles. Decoupled from the Elm VDOM — runs at 60fps without thrash. */
const LEVEL_CSS_VAR = "--audio-level" as const;

type ThrottledFn = () => void;

const throttle = (ms: number, fn: () => void): ThrottledFn => {
  let last = 0;
  return (): void => {
    const now = performance.now();
    if (now - last >= ms) {
      last = now;
      fn();
    }
  };
};

/** Lazy, app-wide Web Audio context. Created on first user-gesture play
 *  (Safari requires this). Reused across <meditation-audio> instances. */
let sharedAudioCtx: AudioContext | null = null;

const getAudioContext = (): AudioContext | null => {
  if (sharedAudioCtx !== null) return sharedAudioCtx;
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (Ctor === undefined) return null;
    // latencyHint: "playback" tells the browser to use LARGER audio buffers
    // and prefer smoothness over latency. This is exactly what we want for
    // meditation playback — we don't need sub-frame audio response, and
    // larger buffers eliminate the popping/glitching that happens on
    // unplugged phones when the main thread occasionally stalls past the
    // default low-latency buffer size.
    sharedAudioCtx = new Ctor({ latencyHint: "playback" });
    return sharedAudioCtx;
  } catch {
    return null;
  }
};

/* iOS policy (see ios.ts): skip Web Audio routing entirely on iOS — WebKit
 * suspends the AudioContext on screen lock and a routed <audio> element goes
 * silent while still reporting `paused: false`. Nobody watches the breath
 * orb with their eyes shut — the analyser only exists for that visual — so
 * playing the element unrouted (native background-audio path) is strictly
 * better for the one thing that matters: the voice not stopping. */

export class MeditationAudio extends HTMLElement {
  readonly #audio: HTMLAudioElement;
  readonly #onTimeUpdate: ThrottledFn;
  readonly #skipAnalyser: boolean;
  #pendingPlay: Promise<void> | null = null;
  #positionPollId: ReturnType<typeof globalThis.setInterval> | null = null;
  #mcTitle = "";

  // "It just cannot stop" — Lisa. The watchdog exists so that any pause or
  // stall we did NOT ask for (screen lock, interruption end, buffer
  // starvation, network blip) gets retried automatically instead of leaving
  // her meditating over dead air.
  #intendedPlaying = false;
  #watchdogId: ReturnType<typeof globalThis.setInterval> | null = null;
  #resumeAttempts = 0;
  #hardRecoveries = 0;
  #lastProgressPos = 0;
  #lastProgressAt = 0;
  /** Seek requested before metadata exists (resume-where-you-left-off on a
   *  fresh page load) — buffered here, applied on `loadedmetadata`. Writing
   *  currentTime at readyState 0 is dropped by several browsers. */
  #pendingSeek: number | null = null;

  // Audio-reactive analyser graph — wired lazily on first play because
  // Safari refuses to construct an AudioContext outside a user gesture.
  // Never wired at all on iOS — see isIOS() above.
  #analyser: AnalyserNode | null = null;
  #freqData: Uint8Array<ArrayBuffer> | null = null;
  #rafId: number | null = null;
  #decayRafId: number | null = null;
  #smoothedLevel = 0;
  #lastWrittenLevel = -1;
  #positionTicks = 0;

  constructor() {
    super();
    this.#skipAnalyser = isIOS();
    this.#audio = new Audio();
    // crossOrigin: required for Web Audio analysis of cross-origin sources
    // (Vercel Blob serves Access-Control-Allow-Origin: * by default, so
    // this stays safe). Without it the AnalyserNode returns silence.
    // Skipped on iOS along with the rest of the analyser graph — an
    // unrouted <audio> element doesn't need crossOrigin at all, and
    // setting it needlessly re-triggers a CORS preflight on some CDNs.
    if (!this.#skipAnalyser) this.#audio.crossOrigin = "anonymous";
    // Eager preload: meditations are ~3MB MP3s; downloading them up-front
    // means click → play is instant (no buffering pop). Browser still
    // honours user data-saver preferences via the Save-Data header.
    this.#audio.preload = "auto";

    this.#audio.addEventListener("loadedmetadata", () => {
      if (this.#pendingSeek !== null) {
        const t = this.#pendingSeek;
        this.#pendingSeek = null;
        if (Number.isFinite(this.#audio.duration)) {
          this.#audio.currentTime = Math.min(t, Math.max(0, this.#audio.duration - 1));
        } else {
          this.#audio.currentTime = t;
        }
      }
      this.dispatchEvent(
        new CustomEvent("durationloaded", {
          detail: { duration: this.#audio.duration } satisfies { duration: number },
        }),
      );
      this.#updateMediaSessionPositionState();
    });

    // Media Session action handlers — registered once. Beyond giving iOS
    // lock-screen controls, REGISTERING these at all is what tells WebKit
    // "this is a real media session, keep it alive in the background,"
    // which is the other half of the iOS fix (the first half is skipping
    // Web Audio routing above). An <audio> element with no Media Session
    // and no visible tab is exactly the profile WebKit throttles hardest.
    if ("mediaSession" in navigator) {
      // Lock-screen play/pause carry USER INTENT — track it so the watchdog
      // honours a deliberate lock-screen pause instead of fighting it.
      navigator.mediaSession.setActionHandler("play", () => {
        this.#intendedPlaying = true;
        this.#resumeAttempts = 0;
        this.#startWatchdog();
        void this.#audio.play();
      });
      navigator.mediaSession.setActionHandler("pause", () => {
        this.#intendedPlaying = false;
        this.#stopWatchdog();
        this.#audio.pause();
      });
      navigator.mediaSession.setActionHandler("seekbackward", (details) => {
        this.#audio.currentTime = Math.max(0, this.#audio.currentTime - (details.seekOffset ?? 15));
      });
      navigator.mediaSession.setActionHandler("seekforward", (details) => {
        this.#audio.currentTime = Math.min(
          this.#audio.duration || Infinity,
          this.#audio.currentTime + (details.seekOffset ?? 15),
        );
      });
      navigator.mediaSession.setActionHandler("seekto", (details) => {
        if (details.fastSeek === true || typeof details.seekTime !== "number") return;
        this.#audio.currentTime = details.seekTime;
      });
    }

    // Fire `started` only when audio is actually producing sound. Elm has
    // already optimistically set AudioPlaying; this is the confirmation that
    // lets the UI know the buffer is full and we are sounding.
    this.#audio.addEventListener("playing", () => {
      // Sound is genuinely flowing again — reset the recovery budget.
      this.#resumeAttempts = 0;
      this.#hardRecoveries = 0;
      this.#lastProgressPos = this.#audio.currentTime;
      this.#lastProgressAt = performance.now();
      this.dispatchEvent(new CustomEvent("started"));
      if (!this.#skipAnalyser) this.#startLevelLoop();
      this.#startPositionPoll();
      this.#setMediaSessionPlaybackState("playing");
    });

    this.#audio.addEventListener("pause", () => {
      // Distinguish a real pause from a transient pause-during-seek (the
      // browser pauses and resumes around currentTime writes). We only act
      // when there's no pending play and we're not at the end. A non-null
      // #pendingSeek means the watchdog's load()+seek hard recovery is in
      // flight — its transient pause must not be read as an interruption.
      if (this.#pendingPlay !== null || this.#pendingSeek !== null || this.#audio.ended) return;
      // Always quiesce the loops — they restart on the next `playing`.
      this.#stopLevelLoop();
      this.#stopPositionPoll();
      this.#setMediaSessionPlaybackState("paused");
      if (this.#intendedPlaying) {
        if (document.hidden) {
          // Involuntary system pause with the screen locked / app in the
          // background (the classic iOS kill Lisa hits ~2 min in). Say
          // nothing to Elm yet — the watchdog and the visibility hook get
          // a recovery window first. If they give up, #surrenderPlayback
          // reports honestly.
          return;
        }
        // Involuntary but visible (headphones unplugged, interruption while
        // she's looking at the screen): honour the platform's pause. Apple
        // pauses on unplug so audio doesn't blast from the speaker — auto-
        // resuming would be actively wrong.
        this.#surrenderPlayback();
        return;
      }
      this.dispatchEvent(new CustomEvent("paused"));
    });

    this.#audio.addEventListener("ended", () => {
      this.dispatchEvent(new CustomEvent("ended"));
      this.#stopLevelLoop();
      this.#stopPositionPoll();
      this.#setMediaSessionPlaybackState("none");
    });

    this.#audio.addEventListener("error", () => {
      this.dispatchEvent(
        new CustomEvent("error", {
          detail: {
            code: this.#audio.error?.code ?? null,
          } satisfies { code: number | null },
        }),
      );
    });

    this.#onTimeUpdate = throttle(TIMEUPDATE_THROTTLE_MS, () => {
      // While the position poll is running it is already publishing at
      // exactly this cadence — firing here too meant Elm received ~8
      // position updates a second and re-diffed the whole player card for
      // every one of them. The poll is the contract (see below); this
      // listener only covers the gaps it doesn't run in (scrubbing while
      // paused, seeking before first play).
      if (this.#positionPollId !== null) return;
      this.#publishPosition();
    });

    this.#audio.addEventListener("timeupdate", this.#onTimeUpdate);

    // Coming back from a locked screen / background tab / BFCache restore:
    // if playback is still intended but the element sits paused, resume it
    // immediately — WebKit permits play() on an element the user has
    // already blessed with a gesture. This is the moment Lisa currently
    // spends "getting back in there"; now the app does it for her.
    document.addEventListener("visibilitychange", this.#onLifecycleResume);
    window.addEventListener("pageshow", this.#onLifecycleResume);
    window.addEventListener("focus", this.#onLifecycleResume);
  }

  readonly #onLifecycleResume = (): void => {
    if (document.hidden || !this.#intendedPlaying) return;
    if (this.#audio.paused && !this.#audio.ended) {
      void this.#audio.play().catch(() => this.#surrenderPlayback());
    }
  };

  set src(url: string) {
    if (this.#audio.src !== url) {
      this.#audio.src = url;
      // Force preload now so the buffer is warm before the user clicks play.
      this.#audio.load();
    }
  }

  /** Named `mcTitle` (not `title`) to avoid colliding with the native
   *  HTMLElement `title` property, which the browser renders as a hover
   *  tooltip — not what we want on an invisible audio element. */
  set mcTitle(t: string) {
    if (this.#mcTitle === t) return;
    this.#mcTitle = t;
    this.#setMediaSessionMetadata();
  }

  set playing(p: boolean) {
    this.#intendedPlaying = p;
    if (p) {
      this.#resumeAttempts = 0;
      this.#hardRecoveries = 0;
      this.#startWatchdog();
      // First-play is a user gesture — set up the analyser graph now so
      // Safari accepts the AudioContext construction.
      this.#ensureAnalyser();
      // Track the pending play so the auto-pause that fires during a seek
      // doesn't get reported as a real pause. AbortError on rapid play/pause
      // toggling is benign — swallow it.
      const promise = this.#audio.play().catch((err: unknown) => {
        if (err instanceof Error && err.name === "AbortError") return;
        throw err;
      });
      this.#pendingPlay = promise;
      void promise.finally(() => {
        if (this.#pendingPlay === promise) this.#pendingPlay = null;
      });
    } else {
      // Deliberate pause — retire the watchdog first so it can't read the
      // pause it is about to observe as a stall and fight the user.
      this.#stopWatchdog();
      this.#audio.pause();
    }
  }

  set position(t: number) {
    if (Math.abs(this.#audio.currentTime - t) > SEEK_THRESHOLD_SECONDS) {
      if (this.#audio.readyState === 0) {
        // No metadata yet (resume-where-you-left-off on a fresh page
        // load) — several browsers silently drop currentTime writes at
        // readyState 0. Buffer it; `loadedmetadata` applies it.
        this.#pendingSeek = t;
      } else {
        this.#audio.currentTime = t;
      }
    }
  }

  set volume(v: number) {
    if (!Number.isFinite(v)) return;
    const clamped = Math.max(0, Math.min(1, v));
    if (Math.abs(this.#audio.volume - clamped) > 0.005) {
      this.#audio.volume = clamped;
    }
  }

  disconnectedCallback(): void {
    this.#intendedPlaying = false;
    this.#stopWatchdog();
    document.removeEventListener("visibilitychange", this.#onLifecycleResume);
    window.removeEventListener("pageshow", this.#onLifecycleResume);
    window.removeEventListener("focus", this.#onLifecycleResume);
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    this.#cancelDecay();
    this.#stopPositionPoll();
    this.#audio.pause();
    this.#audio.src = "";
    // Final reset so a leftover --audio-level can't drive stale visuals.
    this.#smoothedLevel = 0;
    this.#publishLevel(0, true);
    this.#setMediaSessionPlaybackState("none");
  }

  /** The "it just cannot stop" enforcement loop. Runs only while playback
   *  is intended. Two jobs:
   *
   *  1. The element sits paused without the user asking (screen lock,
   *     interruption end, OS reclaim): retry play(), up to
   *     MAX_RESUME_ATTEMPTS between genuine `playing` confirmations.
   *
   *  2. The element claims to be playing but currentTime is frozen
   *     (buffer starvation, dropped connection): after
   *     STALL_THRESHOLD_MS, hard-recover — reload the element, seek back
   *     to where she was, play again. Bounded by MAX_HARD_RECOVERIES.
   *
   *  Both budgets reset on every `playing` event, so a 20-minute session
   *  with three rough patches recovers three times; only a stream that is
   *  truly dead ends in #surrenderPlayback. */
  #startWatchdog(): void {
    if (this.#watchdogId !== null) return;
    this.#lastProgressPos = this.#audio.currentTime;
    this.#lastProgressAt = performance.now();
    this.#watchdogId = globalThis.setInterval(() => {
      this.#watchdogTick();
    }, WATCHDOG_MS);
  }

  #stopWatchdog(): void {
    if (this.#watchdogId !== null) {
      clearInterval(this.#watchdogId);
      this.#watchdogId = null;
    }
  }

  #watchdogTick(): void {
    if (!this.#intendedPlaying || this.#audio.ended) {
      this.#stopWatchdog();
      return;
    }
    if (this.#audio.paused) {
      // Involuntary pause. play() is allowed without a fresh gesture on an
      // element the user already started — retry until the budget runs out.
      if (this.#resumeAttempts >= MAX_RESUME_ATTEMPTS) {
        this.#surrenderPlayback();
        return;
      }
      this.#resumeAttempts += 1;
      void this.#audio.play().catch(() => {
        // Swallow — the next tick retries; the attempt budget is the bound.
      });
      return;
    }
    // Nominally playing — check the clock is actually moving.
    const pos = this.#audio.currentTime;
    const now = performance.now();
    if (pos !== this.#lastProgressPos) {
      this.#lastProgressPos = pos;
      this.#lastProgressAt = now;
      return;
    }
    if (now - this.#lastProgressAt < STALL_THRESHOLD_MS) return;
    // Genuine stall: sound claims to flow, clock says otherwise.
    if (this.#hardRecoveries >= MAX_HARD_RECOVERIES) {
      this.#surrenderPlayback();
      return;
    }
    this.#hardRecoveries += 1;
    this.#pendingSeek = pos;
    this.#audio.load();
    this.#lastProgressAt = now;
    void this.#audio.play().catch(() => {
      // The paused branch above takes over from here if this fails.
    });
  }

  /** Out of road — every retry budget is spent. Stop pretending: retire
   *  the watchdog, let the element rest paused, and tell Elm the truth so
   *  the UI shows a play button instead of a lie. */
  #surrenderPlayback(): void {
    this.#intendedPlaying = false;
    this.#stopWatchdog();
    if (!this.#audio.paused) this.#audio.pause();
    this.#setMediaSessionPlaybackState("paused");
    this.dispatchEvent(new CustomEvent("paused"));
  }

  /** Defensive interval that publishes audio.currentTime to Elm every 250ms.
   *  The native `timeupdate` event is unreliable when an HTMLMediaElement is
   *  routed through Web Audio's createMediaElementSource — some browsers
   *  throttle or suppress it. This setInterval is the contract the slider
   *  depends on, regardless of what the underlying audio graph does. */
  #publishPosition(): void {
    window.dispatchEvent(
      new CustomEvent("mc:timeupdate", {
        detail: {
          id: this.id,
          position: this.#audio.currentTime,
        } satisfies { id: string; position: number },
      }),
    );
  }

  #startPositionPoll(): void {
    if (this.#positionPollId !== null) return;
    // Fire one immediately so the slider starts moving on the first frame.
    this.#publishPosition();
    this.#positionTicks = 0;
    this.#updateMediaSessionPositionState();
    this.#positionPollId = globalThis.setInterval(() => {
      this.#publishPosition();
      this.#positionTicks += 1;
      if (this.#positionTicks % POSITION_STATE_EVERY_N_TICKS === 0) {
        this.#updateMediaSessionPositionState();
      }
    }, TIMEUPDATE_THROTTLE_MS);
  }

  #stopPositionPoll(): void {
    if (this.#positionPollId !== null) {
      globalThis.clearInterval(this.#positionPollId);
      this.#positionPollId = null;
    }
  }

  #setMediaSessionMetadata(): void {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: this.#mcTitle,
      artist: "Lisa Maree Oddy",
      album: "Meditation Compass",
      artwork: [
        { src: "/icons/icon-192.png", sizes: "192x192", type: "image/png" },
        { src: "/icons/icon-512.png", sizes: "512x512", type: "image/png" },
      ],
    });
  }

  #setMediaSessionPlaybackState(state: MediaSessionPlaybackState): void {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.playbackState = state;
  }

  /** Lock-screen scrubber position — iOS only shows one if this is kept
   *  current. Called on duration-known and on every position poll tick. */
  #updateMediaSessionPositionState(): void {
    if (!("mediaSession" in navigator) || typeof navigator.mediaSession.setPositionState !== "function") {
      return;
    }
    const duration = this.#audio.duration;
    if (!Number.isFinite(duration) || duration <= 0) return;
    try {
      navigator.mediaSession.setPositionState({
        duration,
        playbackRate: this.#audio.playbackRate,
        position: Math.min(this.#audio.currentTime, duration),
      });
    } catch {
      // Safari throws if called with a stale/invalid combination during a
      // seek race — harmless, the next poll tick corrects it.
    }
  }

  /** Wire up the Web Audio graph once per element on first play.
   *
   *  PURE PASSTHROUGH (no compressor, no limiter, no makeup). The
   *  prior mastering chain introduced audible distortion across all
   *  platforms — most aggressively on iOS Safari (DynamicsCompressor +
   *  CoreAudio resampling interaction) but also subtly on Android.
   *  Source files are well-encoded (64 kbps opus) and stand on their
   *  own. The audio chain now does ONLY one thing: tap an analyser
   *  for visual reactivity, then pass straight to destination.
   *
   *  Inconsistent loudness across the 24 Lisa files is now a content
   *  problem — fix at the source if needed, not by squeezing it through
   *  the codec at runtime. High fidelity beats loudness consistency. */
  #ensureAnalyser(): void {
    // iOS: NEVER route the element (see ios.ts). Routing has two fatal
    // consequences there: WebKit suspends the AudioContext on screen lock
    // (the silent-death Lisa hit), and — because #skipAnalyser also skips
    // the crossOrigin attribute — a no-CORS R2 stream pulled through
    // createMediaElementSource is spec-mandated SILENCE. The element must
    // stay on the native playback path.
    if (this.#skipAnalyser) return;
    if (this.#analyser !== null) return;
    const ctx = getAudioContext();
    if (ctx === null) return;
    if (ctx.state === "suspended") void ctx.resume();
    try {
      const source = ctx.createMediaElementSource(this.#audio);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = ANALYSER_FFT_SIZE;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      this.#analyser = analyser;
      this.#freqData = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
    } catch {
      // Same-source-twice or CORS failure — fall back to non-reactive mode.
      this.#analyser = null;
    }
  }

  /** Publish the current smoothed level to both consumers.
   *
   *  Frame bus ALWAYS (free — a number assignment), so canvas consumers
   *  see live data without touching the DOM.
   *
   *  :root custom property ONLY when the value actually moved. This is the
   *  expensive half: a custom-property write on documentElement invalidates
   *  style for every descendant. Lisa's meditations are mostly silence
   *  between phrases, where the smoothed level parks at 0.000 — we were
   *  paying a document-wide style invalidation 30×/s to write the same
   *  string back. Elm's own VDOM is property-diffed for exactly this
   *  reason; the audio loop should be too. */
  #publishLevel(level: number, force = false): void {
    publishVoice(level);
    if (!force && Math.abs(level - this.#lastWrittenLevel) < LEVEL_WRITE_EPSILON) return;
    this.#lastWrittenLevel = level;
    document.documentElement.style.setProperty(LEVEL_CSS_VAR, level.toFixed(3));
  }

  #startLevelLoop(): void {
    if (this.#rafId !== null) return;
    // Cancel any in-flight decay so a fast pause→play can't leave two
    // loops fighting over the same level.
    this.#cancelDecay();
    // Throttle the analyser FFT pull + style write. 30 Hz was already well
    // under the ~100ms audio→visual perceptual threshold; on phones we go
    // further, because every frame of this loop is main-thread work
    // competing with audio scheduling on a device that is, per Lisa,
    // already running hot. The breath orb reads identically at 20 Hz.
    const FRAME_MS = getDeviceTier() === "high" ? 33 : 50;
    let lastTick = 0;
    const tick = (now: number): void => {
      if (now - lastTick < FRAME_MS) {
        this.#rafId = requestAnimationFrame(tick);
        return;
      }
      lastTick = now;
      const analyser = this.#analyser;
      const buffer = this.#freqData;
      if (analyser !== null && buffer !== null) {
        analyser.getByteFrequencyData(buffer);
        const start = 2;
        const end = Math.min(buffer.length, 64);
        let sumSq = 0;
        let count = 0;
        for (let i = start; i < end; i += 1) {
          const v = (buffer[i] ?? 0) / 255;
          sumSq += v * v;
          count += 1;
        }
        const rms = count > 0 ? Math.sqrt(sumSq / count) : 0;
        const target = Math.min(1, rms * 1.4);
        this.#smoothedLevel =
          this.#smoothedLevel + (target - this.#smoothedLevel) * LEVEL_SMOOTHING;
        this.#publishLevel(this.#smoothedLevel);
      }
      this.#rafId = requestAnimationFrame(tick);
    };
    this.#rafId = requestAnimationFrame(tick);
  }

  #cancelDecay(): void {
    if (this.#decayRafId !== null) {
      cancelAnimationFrame(this.#decayRafId);
      this.#decayRafId = null;
    }
  }

  #stopLevelLoop(): void {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    this.#cancelDecay();
    // Decay to silence — animation framework expects 0 when not playing.
    // Tracked on its own handle so it can be cancelled by a fast resume.
    const decay = (): void => {
      this.#smoothedLevel *= 0.85;
      if (this.#smoothedLevel < 0.005) {
        this.#smoothedLevel = 0;
        this.#decayRafId = null;
        this.#publishLevel(0, true);
        return;
      }
      this.#publishLevel(this.#smoothedLevel);
      this.#decayRafId = requestAnimationFrame(decay);
    };
    this.#decayRafId = requestAnimationFrame(decay);
  }
}

export const registerMeditationAudio = (): void => {
  if (!customElements.get("meditation-audio")) {
    customElements.define("meditation-audio", MeditationAudio);
  }
};
